// v1.2.0 — what M's Worker knows about notes (Worker 1.1.0, /note/*).
//
// Nothing here is on the money path and nothing here is needed to send, check
// or take a note: every call is best-effort and never throws. The Worker
// records what the app tells it (so the statement can label the rows and a
// tab that died is resolved from the chain, exactly as for a payment) and
// answers two questions the chain can't: are M® Notes switched on, and WHEN a
// note was issued and taken.
//
// What is sent: the wallet, the userOp / tx hash, the amount, the note's
// serial and the address the secret stands for — all of it public on Arc.
// The code, the link secret and the key are never sent, here or anywhere.

// 1.4.34 — the Worker's note answers come from shared/api.ts, one copy for both sides.
import type { NoteConfig, NoteFacts, NoteFee } from "../../../shared/api";
export type { NoteConfig, NoteFacts, NoteFee } from "../../../shared/api";
const BASE: string =
  ((import.meta as { env?: Record<string, string | undefined> }).env?.VITE_WORKER_URL as string | undefined) ?? "http://localhost:8787";

async function call<T>(path: string, init?: RequestInit): Promise<T | null> {
  try {
    const res = await fetch(`${BASE}${path}`, { headers: { "content-type": "application/json" }, ...init });
    return res.ok ? ((await res.json()) as T) : null;
  } catch {
    return null;
  }
}

export interface NoteRecord {
  /** The wallet whose userOp this is: the sender (issue) or the taker (take). */
  address: string;
  kind: "issue" | "take";
  /** Dollars, with cents (v0.5). Sent rounded to the cent. */
  amount: number;
  /** The address the secret stands for — the note's controller before the take. Links an issue to its take. */
  controller: string;
  /** At acceptance. */
  userOpHash?: string;
  /** At the receipt, with `success`. */
  txHash?: string;
  success?: boolean;
  /** The ten symbols. A take knows it from the link; an issue learns it after the receipt (settleSent) — send it then. */
  serial?: string;
}

/**
 * Tell the Worker about a note leg. Call it twice, like recordTx: at the
 * bundler's acceptance (userOpHash) and at the receipt (the same userOpHash,
 * txHash, success — and for an issue, the serial). Either call alone is
 * enough. Resolves true when the Worker took it; never rejects.
 */
export async function recordNote(rec: NoteRecord): Promise<boolean> {
  const body = { ...rec, amount: Math.round(rec.amount * 100) / 100 }; // 14.499999… → 14.5: the Worker refuses fractions of a cent
  return (await call<{ id: number }>("/note/record", { method: "POST", body: JSON.stringify(body) })) !== null;
}


/**
 * When a note was issued and taken, as far as M can prove it. null when the
 * Worker can't be reached or has nothing. Decoration only: whether a note is
 * taken is what Arc says (bySerial), never this.
 */
export function noteFacts(serial: string): Promise<NoteFacts | null> {
  return call<NoteFacts>(`/note/${encodeURIComponent(serial)}`);
}


/** `wallet` (1.4.71) — the fee is per wallet (M may sponsor one's notes). */
export function noteConfig(wallet?: string): Promise<NoteConfig | null> {
  return call<NoteConfig>(wallet ? `/note/config?wallet=${encodeURIComponent(wallet)}` : "/note/config");
}

/**
 * 1.4.71 — the M® Note fee for this wallet's next note, as the Worker gives it. A Worker that can't be reached, or an
 * older one with no `fee`, gives the default (2¢, nobody sponsored, the change to the taker) — a send never waits on it.
 */
export async function noteFee(wallet?: string): Promise<NoteFee> {
  const cfg = await noteConfig(wallet);
  return cfg?.fee ?? { usd: "0.02", sponsored: false, service: null };
}

/**
 * May this build issue notes right now? The build must be switched on
 * (`live`, i.e. VITE_NOTES_ADDRESS) and the Worker must not have switched it
 * off. A Worker that can't be reached does not stop a send: only an explicit
 * `enabled: false` does.
 */
export async function issuingEnabled(live: boolean): Promise<boolean> {
  if (!live) return false;
  const cfg = await noteConfig();
  return cfg ? cfg.configured && cfg.enabled : true;
}
